import React, { useState } from "react";
import "./HomepageEditorial.scss";

const cases = [
  {
    number: "01",
    label: "KoriPass · finance",
    title: "Rendre une transaction fiable, même quand le réseau ne l’est pas.",
    description:
      "Sur KoriPass, j’ai travaillé sur la logique de paiement en Java : une idempotence stricte pour éviter les doubles débits, un ledger add-only pour conserver la trace des débits et crédits, et des webhooks traités de façon sûre.",
    tags: ["Java", "Idempotency", "Webhooks", "Ledger"],
    color: "coral",
  },
  {
    number: "02",
    label: "Cloud & systèmes",
    title: "Passer du prototype à un système que l’on peut opérer.",
    description:
      "Je structure les services, les données et les déploiements pour que le produit reste observable et maintenable : Python pour les traitements, AWS pour l’infrastructure et des API pensées pour les usages réels.",
    tags: ["AWS", "Python", "API", "Observability"],
    color: "blue",
  },
  {
    number: "03",
    label: "Data, IA & IoT",
    title: "Donner de l’intelligence aux produits sans leur enlever leur contrôle.",
    description:
      "Des pipelines Python et Spark aux chatbots LLM, aux recommandations et aux flux IoT, je relie la donnée à une décision utile — avec des garde-fous, des métriques et une vraie responsabilité humaine.",
    tags: ["Python", "ML", "LLM", "IoT"],
    color: "lime",
  },
];

const process = [
  ["01", "Comprendre le risque", "Avant le code, je clarifie les invariants : qui paie, qui reçoit, que se passe-t-il si un webhook arrive deux fois ?"],
  ["02", "Dessiner le système", "Je rends visibles les flux, les états et les responsabilités pour choisir une architecture que l’équipe pourra vraiment opérer."],
  ["03", "Prouver en production", "Je livre par itérations, j’observe les signaux et je laisse derrière moi du code, des tests et des décisions compréhensibles."],
];

const projects = [
  {
    number: "01",
    name: "KoriPass",
    type: "Fintech · plateforme transactionnelle",
    title: "Construire la confiance, transaction après transaction.",
    description:
      "Une expérience de paiement et de services pensée pour rester cohérente quand les appels réseau, les webhooks et les tentatives de paiement se répètent.",
    outcome: "Idempotence · ledger add-only · webhooks",
    image: "/images/works/Koripass/KP_DESC.png",
    color: "coral",
  },
  {
    number: "02",
    name: "PartoAI",
    type: "IA · produit web",
    title: "Rendre une technologie complexe immédiatement utile.",
    description:
      "Une interface qui transforme l’intelligence artificielle en parcours compréhensible : moins de friction, plus de contexte et une expérience conçue pour l’usage.",
    outcome: "React · API · expérience utilisateur",
    image: "/images/works/PartoAi/PA_DESC.png",
    color: "blue",
  },
  {
    number: "03",
    name: "KoriSchool",
    type: "EdTech · plateforme métier",
    title: "Donner une structure simple à un écosystème éducatif.",
    description:
      "Des parcours, des rôles et des informations organisés pour permettre aux apprenants et aux équipes de se concentrer sur l’essentiel.",
    outcome: "Plateforme · rôles · parcours",
    image: "/images/works/Korischool/KS_DESC.png",
    color: "lime",
  },
  {
    number: "04",
    name: "Texsoro",
    type: "Productivité · outil de création",
    title: "Faire passer une idée du fichier au produit.",
    description:
      "Un espace pour importer, éditer et exploiter du contenu avec une attention particulière portée à la clarté des étapes et à la continuité du travail.",
    outcome: "Upload · édition · expérience fluide",
    image: "/images/works/Texsoro/TS_DESC.png",
    color: "paper",
  },
];

const experience = [
  {
    period: "Juil. 2019 — août 2024",
    role: "Développeur Full Stack",
    company: "KoriPass",
    place: "Dakar, Sénégal · Fintech & IoT",
    summary: "J’ai contribué à une plateforme de monnaie électronique où la fiabilité devait être visible : dans chaque transaction, chaque rôle et chaque interaction avec un terminal.",
    achievements: [
      "Développé des dashboards d’administration et de supervision avec React, D3.js et Metabase pour suivre l’activité en temps réel",
      "Sécurisé les transactions par OTP et gestion granulaire des rôles pour contrôler les flux clients, marchands et agents",
      "Intégré Wave, Orange Money et InTouch via API, en traitant les reprises, statuts et erreurs de paiement comme des cas métier",
      "Conçu en équipe un système de paiement NFC breveté sur iOS et Android, avec modules React Native, lecteurs NFC et périphériques Bluetooth",
      "Développé et maintenu des applications React et React Native pour les parcours clients, marchands et agents",
      "Contribué aux règles d’idempotence, au ledger add-only et au traitement sûr des webhooks pour préserver la cohérence financière",
      "Conçu les abonnements pour boulangeries et les programmes de fidélité, puis modélisé les règles de gestion multi-rôles",
      "Collaboré avec une équipe pluridisciplinaire et contribué aux arbitrages produit, aux démonstrations clients, à la documentation et à la formation",
      "Contribué aux choix de conception qui relient identité, rôles, paiements et terminaux afin de garder des flux cohérents de bout en bout",
      "Anticipé les scénarios d’échec et de reprise dans les intégrations de paiement pour rendre les opérations observables et explicables",
    ],
    insight: "Point fort : raisonner en système, relier sécurité, expérience utilisateur, paiements et contraintes hardware dans un produit réglementé.",
    stack: "Java · React · React Native · D3.js · Metabase · OTP · NFC · Bluetooth · JUnit · TestFlight",
  },
  {
    period: "Juil. 2021 — août 2024",
    role: "Lead Developer",
    company: "KoriSchool",
    place: "Dakar, Sénégal · EdTech",
    summary: "J’ai piloté la construction d’outils numériques pour les processus administratifs et pédagogiques, avec un objectif concret : réduire la friction du quotidien scolaire.",
    achievements: [
      "Conçu un système de contrôle des étudiants par QR code et NFC, réduisant de 50 % le temps des appels",
      "Développé une API Java Spring Boot de calcul des taux horaires des enseignants à partir du suivi des heures de cours",
      "Déployé et maintenu les applications sur AWS EC2 et S3, avec une chaîne de livraison mobile via Jira et Expo",
      "Encadré des développeurs, animé les daily meetings et accompagné l’équipe sur Kanban, CI/CD et React.js",
      "Mentoré les développeurs sur React, les pratiques Agile et la livraison incrémentale",
      "Structuré les rôles, les états et les prochaines actions pour rendre les parcours administratifs et pédagogiques lisibles",
      "Séparé clairement expérience utilisateur, API et règles métier afin de garder une plateforme maintenable",
      "Contribué à la conception d’un modèle de données partagé entre présence, heures de cours et calcul de rémunération",
      "Posé des conventions de développement et de livraison pour réduire le couplage et faciliter le travail collectif",
    ],
    insight: "Point fort : leadership technique, conception de flux métier cohérents et responsabilité cloud de bout en bout.",
    stack: "Java · Spring Boot · AWS EC2 · S3 · React · React Native · NFC · QR Code · Jira · Expo · CI/CD",
  },
  {
    period: "Juin 2024 — juin 2026",
    role: "Développeur Full Stack",
    company: "Okooltrip",
    place: "Dakar, Sénégal",
    summary: "Prise en charge d’une plateforme de voyage, de l’architecture à la mise en production, avec une attention particulière portée à la réservation et à la facturation comme flux critiques.",
    achievements: [
      "Conception et développement de la plateforme avec Node.js et Next.js, des parcours publics jusqu’aux flux de réservation : découverte d’une activité, vérification des disponibilités, paiement et confirmation.",
      "Mise en œuvre du rendu serveur pour soutenir le SEO et l’acquisition organique, avec un arbitrage entre indexabilité, performance et fluidité des parcours interactifs.",
      "Déploiement des services sur AWS EC2 avec persistance dans RDS ; externalisation des traitements non bloquants vers SQS et isolement des messages en échec dans une DLQ pour permettre leur reprise contrôlée.",
      "Mise en place d’une architecture résiliente avec Auto Scaling Groups et Application Load Balancer afin d’absorber les variations de trafic sans dépendre d’une instance unique.",
      "Séparation des responsabilités entre contenu, disponibilité, réservation, paiement et facturation afin de limiter le couplage et les effets de bord lors des évolutions.",
      "Conception d’un contrôle de disponibilité idempotent pour empêcher les doubles réservations lors des retries, des requêtes concurrentes ou de la réception répétée d’un webhook.",
      "Modélisation distincte des états de réservation et de paiement, avec notifications automatisées, traçabilité des transitions et reprise explicite jusqu’à la confirmation finale.",
      "Intégré un PSP Mobile Money et renforcé le billing par des traitements asynchrones, des reprises contrôlées et une gestion explicite des erreurs",
    ],
    insight: "Point fort : concevoir un produit transactionnel où performance, résilience AWS et cohérence métier avancent ensemble.",
    stack: "Next.js · Node.js · AWS EC2 · RDS · SQS · DLQ · ASG · ALB · PSP Mobile Money · SSR",
  },
  {
    period: "Mai 2022 — juin 2023",
    role: "Co-fondateur & Développeur Full Stack",
    company: "Fegn SEO",
    place: "Dakar, Sénégal",
    summary: "J’ai co-fondé une agence SEO et relié stratégie d’acquisition, exécution technique et mesure.",
    achievements: [
      "Audits techniques SEO et recommandations priorisées pour les clients",
      "Suivi de performance avec Google Analytics, Search Console et SEMrush",
      "Transformé les audits en plan d’action technique priorisé, reliant performance, architecture de contenu et acquisition",
      "Mis en place une boucle de mesure pour distinguer les recommandations réellement efficaces des optimisations sans impact",
    ],
    insight: "Point fort : capacité à relier architecture de contenu, mesure technique et résultats business.",
    stack: "SEO technique · Analytics · Search Console · SEMrush",
  },
  {
    period: "Fév. 2024 — juil. 2024",
    role: "Mobile Developer",
    company: "Setal Lavage",
    place: "Dakar, Sénégal · Services & mobilité",
    summary: "J’ai développé l’application iPad qui aide les responsables à piloter leurs stations de lavage et leurs services associés.",
    achievements: [
      "Créé les écrans de suivi et de reporting graphique pour les managers",
      "Implémenté la gestion des abonnements et le suivi des clients",
      "Traduit les opérations terrain en workflows mobiles simples et exploitables",
      "Modélisé les états d’un service, d’un abonnement et d’un client pour que les responsables disposent d’une information cohérente",
      "Conçu les flux mobiles autour des contraintes opérationnelles du terrain plutôt qu’autour d’une simple succession d’écrans",
    ],
    insight: "Point fort : capacité à modéliser un workflow opérationnel et à le transformer en outil mobile utile.",
    stack: "iPad · Mobile · Reporting · Abonnements · Suivi client",
  },
  {
    period: "Déc. 2018 — juil. 2019",
    role: "Développeur Web & Intégrateur",
    company: "Groupe Supdeco",
    place: "Dakar, Sénégal · Enseignement supérieur",
    summary: "J’ai travaillé au plus près des équipes métier pour moderniser les outils internes, les sites et la diffusion de contenus de l’école.",
    achievements: [
      "Accéléré de 120 % la recherche interne grâce à des filtres jQuery et JavaScript",
      "Déployé Moodle et Alfresco, puis organisé la diffusion de contenus multimédias sur les écrans",
      "Développé des sites vitrines avec Laravel, Vue.js, Django et jQuery",
      "Animé les recueils de besoins et formé les équipes sur Word, Teams et Excel",
      "Présenté chaque semaine les avancées et recommandations aux responsables, avec des propositions jugées très pertinentes par la direction",
      "Produit des cartes étudiantes et des formulaires de sondage adaptés aux besoins administratifs",
      "Contribué à l’intégration cohérente des outils internes, des contenus et des sites afin de simplifier les usages des équipes",
      "Traduit les besoins recueillis auprès des managers en solutions maintenables, documentées et suffisamment simples à transmettre",
    ],
    insight: "Point fort : première expérience de terrain combinant intégration, conception maintenable, support et dialogue avec les utilisateurs.",
    stack: "Laravel · Vue.js · Django · jQuery · Moodle · Alfresco · JavaScript",
  },
];

const researchProjects = [
  {
    title: "Recommander les bonnes activités au bon voyageur",
    context: "Data Science · UQO, Gatineau · sept. — déc. 2024 · application Okooltrip",
    objective: "Exploiter les profils comportementaux pour recommander des activités et des tours pertinents dans une newsletter Okooltrip.",
    method: "K-means pour segmenter les profils, distances cosinus pour mesurer la proximité et pipeline Python reproductible pour générer des recommandations.",
    result: "Un moteur de recommandation interprétable, capable de transformer des signaux comportementaux en suggestions éditoriales personnalisées.",
    tools: "Python · Pandas · NumPy · Scikit-learn · K-means · Cosine similarity · Newsletter · Data visualization",
    color: "blue",
  },
  {
    title: "Prédiction de la spontanéité à partir de signaux biométriques",
    context: "Machine Learning · projet de recherche appliquée",
    objective: "Prédire la spontanéité d’une personne à partir de caractéristiques physiologiques, visuelles et faciales.",
    method: "Comparaison de Gradient Boosting, SVM et Random Forest avec évaluation comparative des performances.",
    result: "Gradient Boosting atteint 80 % de précision, fournissant une base solide pour approfondir les variables les plus prédictives.",
    tools: "Python · Scikit-learn · Feature engineering · Gradient Boosting · SVM · Random Forest · Model evaluation",
    color: "lime",
  },
];

const capabilityGroups = [
  {
    label: "Construire",
    title: "Des interfaces qui tiennent le système.",
    copy: "Du dashboard web à l’application mobile, les interfaces React et React Native restent alignées avec les API, les états métier et les contraintes du terrain.",
    tools: "React · React Native · JavaScript · TypeScript · Expo",
    color: "coral",
  },
  {
    label: "Orchestrer",
    title: "Des backends qui portent le produit.",
    copy: "Conception d’API et de services métier capables de gérer les rôles, les transactions, les réservations et les intégrations sans déplacer la complexité vers l’utilisateur.",
    tools: "Java · Spring Boot · Node.js · Next.js · REST API",
    color: "blue",
  },
  {
    label: "Faire évoluer",
    title: "Des données et des flux maîtrisés.",
    copy: "De la persistance aux traitements asynchrones, chaque flux est pensé pour rester traçable, rejouable et cohérent quand le volume ou les erreurs augmentent.",
    tools: "PostgreSQL · RDS · SQS · DLQ · Idempotency · Webhooks",
    color: "lime",
  },
  {
    label: "Déployer",
    title: "Du code à un service opérable.",
    copy: "Les choix cloud relient performance, disponibilité et exploitation : déployer, observer, reprendre un échec et faire évoluer la capacité font partie du produit.",
    tools: "AWS · EC2 · ALB · ASG · S3 · CI/CD · Monitoring",
    color: "paper",
  },
  {
    label: "Faire grandir",
    title: "Le code, mais jamais seul.",
    copy: "Je facilite les daily meetings, le mentorat et les décisions partagées pour aider une équipe à livrer avec plus d’autonomie.",
    tools: "Mentorat · Agile · Kanban · CI/CD · Code review",
    color: "coral",
  },
  {
    label: "Relier",
    title: "Bilingue dans les échanges.",
    copy: "Français et anglais au quotidien pour clarifier un besoin, documenter une décision et collaborer avec des profils techniques et métier.",
    tools: "Français · English · Documentation · Workshops",
    color: "blue",
  },
];

function Homepage() {
  const [activeCase, setActiveCase] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="editorial-home">
      <header className="editorial-nav">
        <a className="editorial-brand" href="#top" onClick={closeMenu} aria-label="Retour en haut">
          <span className="brand-mark" aria-hidden="true">
            <svg viewBox="0 0 42 42" focusable="false">
              <path d="M12 12.5c2.8-3.1 8.1-3.3 10.3-.1 2.2 3.2-.6 5.2-3.8 7.1l-4.1 2.4c-3.8 2.2-3.5 6.1.1 7.8 3.4 1.6 8.2.3 10.7-2.6" />
              <path d="M27.5 11.5 22 22.2c-1.6 3.1-1 6.7 2.2 8.1 2.8 1.2 6.2-.3 7.6-3.1L37 16.8" />
            </svg>
          </span>
          <span>Yaya Sangaré</span>
        </a>
        <button
          className="editorial-menu-button"
          type="button"
          aria-expanded={menuOpen}
          aria-controls="editorial-menu"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span>{menuOpen ? "Fermer" : "Menu"}</span>
          <i className={menuOpen ? "menu-lines is-open" : "menu-lines"} aria-hidden="true" />
        </button>
        <nav id="editorial-menu" className={menuOpen ? "editorial-menu is-open" : "editorial-menu"}>
          <a href="#approche" onClick={closeMenu}>Approche</a>
          <a href="#cas" onClick={closeMenu}>Cas d’usage</a>
          <a href="#experience" onClick={closeMenu}>Expériences</a>
          <a href="#recherche" onClick={closeMenu}>Recherche</a>
          <a href="#competences" onClick={closeMenu}>Compétences</a>
          <a href="#portfolio" onClick={closeMenu}>Portfolio</a>
          <a href="#parcours" onClick={closeMenu}>Parcours</a>
          <a className="nav-talk" href="#contact" onClick={closeMenu}>Parlons-nous <span>↗</span></a>
        </nav>
      </header>

      <main id="top">
        <section className="editorial-hero">
          <div className="hero-copy">
            <p className="eyebrow"><span className="eyebrow-dot" /> Full Stack · Web & Mobile · FR / EN</p>
            <h1>Je construis des systèmes <em>auxquels on peut</em> faire confiance.</h1>
            <p className="hero-intro">
              Depuis 7 ans, je transforme des besoins métier complexes en applications web et mobiles robustes — avec React, React Native, Java, Python et AWS, et une obsession : que le système fasse juste, même sous pression.
            </p>
            <div className="hero-actions">
              <a className="button button-dark" href="#cas">Voir mon travail <span>↓</span></a>
              <a className="text-link" href="/images/hero/ResumYaya.pdf" download>Mon parcours <span>↗</span></a>
            </div>
          </div>
          <div className="hero-portrait" aria-label="Portrait de Yaya Sangaré">
            <div className="portrait-orbit orbit-one" />
            <div className="portrait-orbit orbit-two" />
            <div className="portrait-card">
              <img src="/images/card.png" alt="Portrait illustré de Yaya Sangaré" />
            </div>
            <div className="portrait-note"><span>✦</span> Curiosité<br />en mouvement</div>
            <div className="portrait-stamp">BUILD<br />WITH<br />CARE</div>
          </div>
          <div className="hero-scroll"><span>↓</span> Faites défiler</div>
        </section>

        <section className="proof-strip" aria-label="Repères">
          <p>La confiance n’est pas une promesse.<br /><strong>C’est une propriété du système.</strong></p>
          <div className="proof-item"><strong>7+</strong><span>ans à construire<br />des produits web</span></div>
          <div className="proof-item"><strong>React</strong><span>web, dashboards<br />et design systems</span></div>
          <div className="proof-item"><strong>RN</strong><span>mobile, NFC<br />et iOS / Android</span></div>
          <div className="proof-item"><strong>Java</strong><span>transactions, APIs<br />et règles métier</span></div>
          <div className="proof-item"><strong>Node.js</strong><span>services web<br />et flux asynchrones</span></div>
          <div className="proof-item"><strong>Python</strong><span>data, ML<br />et automatisation</span></div>
          <div className="proof-item"><strong>PHP</strong><span>plateformes web<br />et intégrations métier</span></div>
          <div className="proof-item"><strong>FR / EN</strong><span>équipes, clients<br />et documentation</span></div>
        </section>

        <section className="story-section" id="approche">
          <div className="section-kicker">01 / Ma façon de faire</div>
          <div className="story-grid">
            <h2>La confiance se construit<br /><em>dans les détails.</em></h2>
            <div>
            <p className="large-copy">Un paiement peut être rejoué. Un webhook peut arriver deux fois. Un capteur peut envoyer une donnée en retard. Un bon système ne nie pas ces scénarios : il les prévoit.</p>
            <p>C’est le fil rouge de mon parcours : des plateformes web et mobiles, des flux data, des transactions financières et des fonctionnalités IA qui doivent rester cohérents quand le monde réel devient imprévisible.</p>
            <a className="text-link" href="#experience">Voir les preuves dans mon parcours <span>↗</span></a>
            </div>
          </div>
          <div className="process-list">
            {process.map(([number, title, copy]) => (
              <div className="process-item" key={number}>
                <span className="process-number">{number}</span>
                <h3>{title}</h3>
                <p>{copy}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="cases-section" id="cas">
          <div className="section-heading">
            <div>
              <div className="section-kicker">02 / Cas d’usage</div>
              <h2>Des problèmes réels.<br /><em>Des réponses utiles.</em></h2>
            </div>
            <p>Quelques situations où design, produit et ingénierie se rencontrent pour créer une différence durable.</p>
          </div>
          <div className="case-layout">
            <div className="case-tabs" role="tablist" aria-label="Cas d’usage">
              {cases.map((item, index) => (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeCase === index}
                  className={activeCase === index ? "case-tab is-active" : "case-tab"}
                  key={item.number}
                  onClick={() => setActiveCase(index)}
                >
                  <span>{item.number}</span>{item.label}<b>↗</b>
                </button>
              ))}
            </div>
            <article className={`case-feature case-${cases[activeCase].color}`}>
              <div className="case-feature-top"><span>Use case {cases[activeCase].number}</span><span>● En pratique</span></div>
              <h3>{cases[activeCase].title}</h3>
              <p>{cases[activeCase].description}</p>
              <div className="case-tags">{cases[activeCase].tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
              <div className="case-graphic" aria-hidden="true"><span>✦</span><i /><b /></div>
            </article>
          </div>
        </section>

        <section className="experience-section" id="experience">
          <div className="section-heading experience-heading">
            <div>
              <div className="section-kicker">03 / Expériences</div>
              <h2>Des produits où<br /><em>la confiance se voit.</em></h2>
            </div>
            <p>Deux projets centraux montrent ma façon de travailler : sécuriser les transactions financières et rendre un produit métier compréhensible, maintenable et prêt à évoluer.</p>
          </div>
          <div className="experience-list">
            {experience.map((item) => (
              <article className="experience-item" key={`${item.company}-${item.role}`}>
                <div className="experience-meta">
                  <span>{item.period}</span>
                  <span>{item.place}</span>
                </div>
                <div className="experience-main">
                  <h3>{item.role}</h3>
                  <div className="experience-company">{item.company}</div>
                  <p className="experience-summary">{item.summary}</p>
                  <ul>{item.achievements.map((achievement) => <li key={achievement}>{achievement}</li>)}</ul>
                  <p className="experience-insight">{item.insight}</p>
                  <div className="experience-stack">{item.stack}</div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="research-section" id="recherche">
          <div className="section-heading research-heading">
            <div>
              <div className="section-kicker">04 / Recherche appliquée</div>
              <h2>Mesurer avant<br /><em>d’affirmer.</em></h2>
            </div>
            <p>La data science complète mon expérience produit : formuler une hypothèse, comparer les modèles et communiquer une limite aussi clairement qu’un résultat.</p>
          </div>
          <div className="research-grid">
            {researchProjects.map((project) => (
              <article className={`research-card research-${project.color}`} key={project.title}>
                <div className="research-card-top">{project.context}<span>✦</span></div>
                <h3>{project.title}</h3>
                <div className="research-block"><strong>Objectif</strong><p>{project.objective}</p></div>
                <div className="research-block"><strong>Méthode</strong><p>{project.method}</p></div>
                <div className="research-block"><strong>Résultat</strong><p>{project.result}</p></div>
                <div className="research-tools">{project.tools}</div>
              </article>
            ))}
          </div>
        </section>

        <section className="capabilities-section" id="competences">
          <div className="section-heading capabilities-heading">
            <div>
              <div className="section-kicker">05 / Ce que j’apporte</div>
              <h2>Plus qu’une stack.<br /><em>Une façon de travailler.</em></h2>
            </div>
            <p>Les outils comptent. La capacité à les mettre au service d’une équipe, d’un utilisateur et d’un objectif compte davantage.</p>
          </div>
          <div className="capability-grid">
            {capabilityGroups.map((capability) => (
              <article className={`capability-card capability-${capability.color}`} key={capability.label}>
                <span className="capability-label">{capability.label}</span>
                <h3>{capability.title}</h3>
                <p>{capability.copy}</p>
                <div className="capability-tools">{capability.tools}</div>
              </article>
            ))}
          </div>
        </section>

        <section className="portfolio-section" id="portfolio">
          <div className="section-heading portfolio-heading">
            <div>
              <div className="section-kicker">06 / Portfolio</div>
              <h2>Ce que les idées<br /><em>deviennent.</em></h2>
            </div>
            <p>Quelques produits construits au fil du parcours : chacun répond à un problème différent, mais tous racontent la même exigence.</p>
          </div>
          <div className="project-grid">
            {projects.map((project) => (
              <article className={`project-card project-${project.color}`} key={project.name}>
                <div className="project-card-top">
                  <span>{project.number} / {project.type}</span>
                  <span className="project-arrow">↗</span>
                </div>
                <div className="project-image">
                  <img src={project.image} alt={`Aperçu du projet ${project.name}`} />
                </div>
                <div className="project-content">
                  <h3>{project.name}</h3>
                  <h4>{project.title}</h4>
                  <p>{project.description}</p>
                  <span className="project-outcome">{project.outcome}</span>
                </div>
              </article>
            ))}
          </div>
          <div className="portfolio-more">
            <span>Chaque projet est une conversation entre le besoin, le système et la personne qui l’utilise.</span>
            <a className="text-link" href="#contact">Parler d’un projet <span>↗</span></a>
          </div>
        </section>

        <section className="journey-section" id="parcours">
          <div className="journey-art" role="img" aria-label="Composition 3D représentant les systèmes fiables, le cloud, les données et le produit">
            <div className="art-glow" />
            <div className="art-orbit art-orbit-one"><span className="art-node node-java">Java</span></div>
            <div className="art-orbit art-orbit-two"><span className="art-node node-react">React</span></div>
            <div className="art-orbit art-orbit-three"><span className="art-node node-aws">AWS</span></div>
            <div className="art-core">
              <span className="core-symbol">✦</span>
              <strong>BUILD<br />WITH<br />CARE</strong>
            </div>
            <div className="art-label">Systèmes fiables<br /><span>dans le monde réel</span></div>
          </div>
          <div className="journey-copy">
            <div className="section-kicker">07 / Le parcours</div>
            <h2>Du besoin métier<br />à la <em>mise en production.</em></h2>
            <p>Mon fil rouge est simple : comprendre le risque avant de choisir la technologie, puis rester responsable du résultat jusqu’à sa mise en production. Cette expérience couvre AWS, DigitalOcean et l’administration d’environnements VPS, avec une attention portée au déploiement, à la disponibilité et à l’exploitation quotidienne.</p>
            <p>Cette approche m’a amené à travailler sur des plateformes de réservation, des produits de paiement, des outils de segmentation, des pipelines data et des fonctionnalités IA utilisées par de vrais utilisateurs.</p>
          </div>
        </section>

        <section className="contact-section" id="contact">
          <div className="contact-star">✦</div>
          <div className="section-kicker">08 / La suite</div>
          <h2>Vous avez un sujet<br /><em>qui mérite mieux ?</em></h2>
          <p>Parlez-moi de ce que vous construisez, de ce qui bloque ou de ce qui pourrait être plus simple.</p>
          <a className="button button-dark contact-button" href="mailto:sayotagt@gmail.com">
            Me contacter <span>↗</span>
          </a>
        </section>
      </main>
      <footer className="editorial-footer">
        <span>© {new Date().getFullYear()} Yaya Sangaré</span>
        <span>Fait avec intention, curiosité et beaucoup de café.</span>
        <a href="#top">Retour en haut ↑</a>
      </footer>
    </div>
  );
}

export default Homepage;
